import { HeaderPage } from "@/components/HeaderPage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Calendar,
  User,
  MessageSquare,
  MapPin,
  Loader2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useGetNotificationById } from "@/hooks/useNotifications";
import { formatDate } from "@/lib/utils";
import { motion } from "framer-motion";

export const NotificationView = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: notification, isLoading } = useGetNotificationById(id!);

  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      GENERAL_ANNOUNCEMENT: {
        label: "Anúncio Geral",
        color: "bg-blue-100 text-blue-700",
      },
      NEIGHBORHOOD_ANNOUNCEMENT: {
        label: "Anúncio de Bairro",
        color: "bg-green-100 text-green-700",
      },
      OCCURRENCE_UPDATE: {
        label: "Atualização de Ocorrência",
        color: "bg-purple-100 text-purple-700",
      },
      CITY_HALL_RESPONSE: {
        label: "Resposta da Prefeitura",
        color: "bg-indigo-100 text-indigo-700",
      },
      USER_ANNOUNCEMENT: {
        label: "Usuário",
        color: "bg-orange-100 text-orange-700",
      },
      GENERAL: { label: "Geral", color: "bg-blue-100 text-blue-700" },
      NEIGHBORHOOD: { label: "Bairro", color: "bg-green-100 text-green-700" },
      USER: { label: "Usuário", color: "bg-purple-100 text-purple-700" },
    };
    return typeMap[type] || { label: type, color: "bg-gray-100 text-gray-700" };
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <Loader2 className="h-12 w-12 animate-spin text-emerald-600" />
        <p className="text-lg font-medium text-gray-700">
          Carregando notificação...
        </p>
      </div>
    );
  }

  if (!notification) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <MessageSquare className="h-12 w-12 text-gray-400" />
        <p className="text-lg font-medium text-gray-700">
          Notificação não encontrada
        </p>
        <Button onClick={() => navigate("/notifications")} variant="outline">
          Voltar para Notificações
        </Button>
      </div>
    );
  }

  const typeInfo = getTypeLabel(notification.type);

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/notifications")}
            className="h-10 w-10"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <HeaderPage
            title="Detalhes da Notificação"
            description="Visualize as informações da notificação enviada."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-10">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Card de Informações Principais */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    Informações da Notificação
                  </h3>
                  <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Título</p>
                    <p className="font-semibold text-lg text-gray-900">
                      {notification.title}
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-500 mb-2">Mensagem</p>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {notification.message}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Card de Destinatário (se aplicável) */}
          {notification.type === "NEIGHBORHOOD" &&
            notification.neighborhoodId && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card className="bg-white">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4">Destinatário</h3>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-green-700" />
                      </div>
                      <div>
                        <p className="font-medium">Bairro</p>
                        <p className="text-sm text-gray-500">
                          ID: {notification.neighborhoodId}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

          {notification.type === "USER" && notification.userId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Destinatário</h3>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-700" />
                    </div>
                    <div>
                      <p className="font-medium">Usuário Específico</p>
                      <p className="text-sm text-gray-500">
                        ID: {notification.userId}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {notification.type === "GENERAL" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-white">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Destinatário</h3>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-blue-700" />
                    </div>
                    <div>
                      <p className="font-medium">Todos os Usuários</p>
                      <p className="text-sm text-gray-500">
                        Notificação enviada para todos os usuários do sistema
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </div>

        {/* Coluna Lateral */}
        <div className="space-y-6">
          {/* Card de Informações Adicionais */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Detalhes</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-500">Criada em</p>
                      <p className="font-medium">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                  </div>

                  {notification.isRead && notification.readAt && (
                    <div className="flex items-start gap-3 pt-3 border-t">
                      <User className="h-5 w-5 text-gray-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Lida em</p>
                        <p className="font-medium">
                          {formatDate(notification.readAt)}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="pt-3 border-t">
                    <div className="flex items-center gap-2">
                      <div
                        className={`h-2 w-2 rounded-full ${notification.isRead ? "bg-green-500" : "bg-yellow-500"}`}
                      ></div>
                      <p className="text-sm font-medium">
                        {notification.isRead ? "Lida" : "Não lida"}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </>
  );
};
